import { SessionModel, SessionService, SessionRepository } from '../session';
import { catchError, finalize, first, map, switchMap } from 'rxjs/operators';
import { Http } from '../../core/http';
import { BasketApi } from './basket.api';
import { BasketHelper, BasketModel, createBasket } from './model';
import { BasketRepository } from './store';
import {
  BasketSetExtraBody,
  BasketSetRoomBody,
} from './model/basket.interface';
import { BasketHelperService } from './basket-helper.service';
import { of, zip } from 'rxjs';
import { RoomModel, RoomRepository } from '../room';

const fetchBasket = (session: SessionModel) => {
  BasketRepository.setLoadingInBasket(true);
  return BasketHelperService.fetchGetBasket(session).pipe(
    first(),
    map((basketResponse) => {
      const basket = createBasket(basketResponse, session.display.currency);
      BasketRepository.updateBasket(basket);
      BasketRepository.setLoadingInBasket(false);
      return basket;
    }),
    catchError((err) => {
      return of(err as BasketModel);
    })
  );
};

const fetchDeleteBasket = (sessionId: SessionModel['sessionId']) => {
  return Http.delete<void>(BasketApi.deleteBasket(sessionId));
};

const setRoomInBasket = (body: BasketSetRoomBody) => {
  BasketRepository.setLoadingInBasket(true);
  const previousBasket = BasketRepository.store.getValue();
  if (
    body.quantity > 0 &&
    !previousBasket.rooms.find((r) => r.roomId === body.roomId)
  ) {
    BasketRepository.updateBasket(createBasketFromBasketSetRoom(body));
  }
  return SessionRepository.sessionLoaded$.pipe(
    first(),
    switchMap((session) =>
      zip(
        of(session),
        BasketHelperService.fetchSetRoomInBasket(session.sessionId, body)
      )
    ), //TODO REVIEW
    switchMap(([session]) =>
      BasketHelperService.updateBasketAndSession(session)
    ),
    catchError((err) => {
      BasketRepository.updateBasket(previousBasket);
      return SessionService.shouldBeSessionError(err);
    }),
    finalize(() => BasketRepository.setLoadingInBasket(false))
  );
};

const createBasketFromBasketSetRoom = (body: BasketSetRoomBody) => {
  const currentBasket = BasketRepository.store.getValue();
  const room = RoomRepository.getRooms().find((r) => r.roomId === body.roomId);
  if (room) {
    const roomWithSelectedOccupancy: RoomModel = {
      ...room,
      occupancies:
        room.occupancies.filter((o) => o.occupancyId === body.occupancyId) ||
        room.occupancies,
    };
    currentBasket.rooms.push(
      BasketHelper.roomToBasketRoom(
        roomWithSelectedOccupancy,
        body.rateId,
        body.quantity
      )
    );
  }
  return currentBasket;
};

const setExtraInBasket = (body: BasketSetExtraBody) => {
  BasketRepository.setLoadingInBasket(true);
  return SessionRepository.sessionLoaded$.pipe(
    first(),
    switchMap((session) =>
      zip(
        of(session),
        BasketHelperService.fetchSetExtraInBasket(session.sessionId, body)
      )
    ),
    switchMap(([session]) =>
      BasketHelperService.updateBasketAndSession(session)
    ),
    catchError(SessionService.shouldBeSessionError),
    finalize(() => BasketRepository.setLoadingInBasket(false))
  );
};

const getBasket = (session: SessionModel) => {
  if (BasketRepository.store.getValue().price) {
    BasketRepository.setLoadingInBasket(false);
    return BasketRepository.basketWithPrice$;
  }

  return fetchBasket(session);
};

const deleteBasket = () => {
  BasketRepository.setLoadingInBasket(true);
  return SessionRepository.sessionLoaded$.pipe(
    first(),
    switchMap((session) =>
      zip(of(session), fetchDeleteBasket(session.sessionId))
    ),
    switchMap(([session]) =>
      BasketHelperService.updateBasketAndSession(session)
    ),
    catchError(SessionService.shouldBeSessionError),
    finalize(() => BasketRepository.setLoadingInBasket(false))
  );
};

const resetCacheFetchGetBasket = () =>
  BasketHelperService.resetCacheFetchGetBasket();

export const BasketService = {
  fetchBasket,
  fetchDeleteBasket,
  getBasket,
  setRoomInBasket,
  setExtraInBasket,
  deleteBasket,
  resetCacheFetchGetBasket,
};
